import { useMutation } from '@tanstack/react-query';
import request from 'lib/request';
import type { FailedApiResponse } from 'components/well-page-content/types';
import type {
  WellborePointsResponse,
  WellboreDatumUpdate,
} from 'types/wellbore-geometry-workspace';

export interface UpdateWellboreDatum {
  /** True while the write is in flight, to hold the drawer's Save. */
  saving: boolean;
  /** Why the last save was refused, in the backend's words, or null. */
  error: string | null;
  /** Sends the two elevations. Either may be omitted and is then left as it is. */
  save: (datumUpdate: WellboreDatumUpdate) => void;
  /** Clears the last refusal. */
  reset: () => void;
}

interface Options {
  /** The bore's datum write address. */
  href: string;
  /** The answer is the whole points payload, re-referenced through the new datum. */
  onSaved: (points: WellborePointsResponse) => void;
}

const DATUM_NOT_SAVED = 'The elevations were not saved.';

/**
 * POST …/datum_updates — Property Details (Story 5, INS-12477). Synchronous, answering
 * with the re-referenced points; a 422 names the rule the values broke, surfaced as-is.
 * @param {Options} options The write address and what to do once it lands.
 * @return {UpdateWellboreDatum} The save, its state and its refusal.
 */
export const useUpdateWellboreDatum = ({
  href,
  onSaved,
}: Options): UpdateWellboreDatum => {
  const { mutate, isPending, error, reset } = useMutation<
    WellborePointsResponse,
    Error,
    WellboreDatumUpdate
  >({
    mutationFn: async (datumUpdate) => {
      const result = await request.post<
        WellborePointsResponse,
        FailedApiResponse
      >(href, { datum_update: datumUpdate }, new AbortController());
      if (result.type === 'success') return result.data;
      throw new Error(
        result.type === 'error'
          ? (result.data?.errors?.[0]?.message ?? DATUM_NOT_SAVED)
          : DATUM_NOT_SAVED
      );
    },
    // A refusal (422) is an answer about the values, not a blip to retry through.
    retry: false,
    onSuccess: onSaved,
  });

  return {
    saving: isPending,
    error: error?.message ?? null,
    save: (datumUpdate) => mutate(datumUpdate),
    reset,
  };
};
